/**
 * Validation utilities for form inputs.
 *
 * Centralized validation functions to ensure consistency across the application
 * and reduce code duplication. Provides email and username validation functions
 * used in authentication forms and other input fields.
 *
 * @module validation
 */

/**
 * Validates an email address using a standard regex pattern
 * 
 * @param email - Email address to validate
 * @returns true if email is valid, false otherwise
 * 
 * @example
 * ```typescript
 * if (isValidEmail(userInput)) {
 *   // Email is valid
 * } else {
 *   // Show error message
 * }
 * ```
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validates a username format
 * 
 * Username requirements:
 * - 3-50 characters in length
 * - Only letters, numbers, underscores, or hyphens
 * 
 * @param username - Username to validate
 * @returns true if username is valid, false otherwise
 * 
 * @example
 * ```typescript
 * if (isValidUsername(userInput)) {
 *   // Username is valid
 * } else {
 *   // Show error message
 * }
 * ```
 */
export function isValidUsername(username: string): boolean {
  return (
    username.length >= 3 &&
    username.length <= 50 &&
    /^[a-zA-Z0-9_-]+$/.test(username)
  );
}

